import { Box, Chip, Fade, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import { glassCard } from '../../designsystem/tokens';
import VoiceInput from './VoiceInput';

const Task = ({task, setTask, loaded, setNextDisabled}) => {
    const { t } = useTranslation();
    const [newTask, setNewTask] = React.useState({
        subject: task.subject || task.title || '',
        priority: task.priority || 'NONE',
        dueDate: task.dueDate || task.due_date || dayjs().format("YYYY-MM-DDTHH:mm:ss.SSS"),
        hs_task_type: task.hs_task_type || '',
        description: task.description || '',
        status: (task.status === 'pending' || task.status === 'in_progress') ? 'NOT_STARTED' : (task.status || 'NOT_STARTED'),
    });

    const validate = (data) => {
        const isValid = data.subject && data.subject.trim() !== "";
        setNextDisabled(!isValid);
    };

    useEffect(() => {
        validate(newTask);
        loaded();
    }, [newTask]);

    const taskTypes = [
        { value: 'CALL', icon: <PhoneIcon />, label: t('call') },
        { value: 'EMAIL', icon: <EmailIcon />, label: t('email') },
        { value: 'TODO', icon: <CheckBoxIcon />, label: t('todo') },
    ];

    const handleUpdate = (updates) => {
        const newData = { ...newTask, ...updates, fixed: true };
        setNewTask(newData);
        setTask(newData);
        validate(newData);
    };

    return (
        <Fade in timeout={300}>
            <Stack spacing={2.5} sx={{ width: '100%' }}>

                {/* Card 1: Subject (required, own card) */}
                <Fade in timeout={350} style={{ transitionDelay: '50ms' }}>
                    <Box sx={glassCard}>
                        <TextField
                            fullWidth
                            data-testid="task-subject"
                            label={t('subject')}
                            required
                            error={!newTask.subject || newTask.subject.trim() === ""}
                            helperText={(!newTask.subject || newTask.subject.trim() === "") ? (t('mandatoryField') || t('required')) : ''}
                            multiline
                            value={newTask.subject}
                            onChange={(event) => handleUpdate({ subject: event.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <VoiceInput
                                        value={newTask.subject}
                                        onResult={(text) => handleUpdate({ subject: text })}
                                    />
                                )
                            }}
                        />
                    </Box>
                </Fade>

                {/* Card 2: Type chips + Priority/Status side by side */}
                <Fade in timeout={350} style={{ transitionDelay: '100ms' }}>
                    <Box sx={glassCard}>
                        <Stack spacing={2}>
                            {/* Task type as Chip pills */}
                            <Stack spacing={0.5}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    {t('hsTaskType')}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    {taskTypes.map(opt => (
                                        <Chip key={opt.value} icon={opt.icon} label={opt.label}
                                            data-testid={`task-type-${opt.value.toLowerCase()}`}
                                            onClick={() => handleUpdate({ hs_task_type: opt.value })}
                                            sx={{
                                                flex: 1, height: 48, borderRadius: '24px',
                                                fontWeight: newTask.hs_task_type === opt.value ? 700 : 400,
                                                backgroundColor: newTask.hs_task_type === opt.value ? '#1f7a7a' : 'rgba(255,255,255,0.08)',
                                                color: newTask.hs_task_type === opt.value ? '#fff' : 'rgba(255,255,255,0.9)',
                                                border: newTask.hs_task_type === opt.value ? '2px solid #1f7a7a' : '1px solid rgba(255,255,255,0.3)',
                                                '& .MuiChip-icon': {
                                                    color: newTask.hs_task_type === opt.value ? '#fff' : 'rgba(255,255,255,0.7)',
                                                },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>

                            {/* Priority + Status side by side */}
                            <Stack direction="row" spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <InputLabel id="priority-label">{t('priority')}</InputLabel>
                                    <Select
                                        labelId="priority-label"
                                        data-testid="task-priority"
                                        label={t('priority')}
                                        defaultValue={newTask.priority}
                                        sx={{ minHeight: 48 }}
                                        onChange={(event) => handleUpdate({ priority: event.target.value })}
                                    >
                                        <MenuItem value="NONE" sx={{ minHeight: 48 }}>{t('none')}</MenuItem>
                                        <MenuItem value="LOW" sx={{ minHeight: 48 }}>{t('low')}</MenuItem>
                                        <MenuItem value="MEDIUM" sx={{ minHeight: 48 }}>{t('medium')}</MenuItem>
                                        <MenuItem value="HIGH" sx={{ minHeight: 48 }}>{t('high')}</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <InputLabel id="status-label">{t('status')}</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        data-testid="task-status"
                                        label={t('status')}
                                        defaultValue={newTask.status}
                                        sx={{ minHeight: 48 }}
                                        onChange={(event) => handleUpdate({ status: event.target.value })}
                                    >
                                        <MenuItem value="NOT_STARTED" sx={{ minHeight: 48 }}>{t('notStarted')}</MenuItem>
                                        <MenuItem value="COMPLETED" sx={{ minHeight: 48 }}>{t('completed')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </Box>
                </Fade>

                {/* Card 3: Date + Description */}
                <Fade in timeout={350} style={{ transitionDelay: '150ms' }}>
                    <Box sx={glassCard}>
                        <Stack spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileDateTimePicker
                                    data-testid="task-duedate"
                                    label={t('dueDate')}
                                    value={newTask.dueDate ? dayjs(newTask.dueDate).tz(dayjs.tz.guess()) : dayjs()}
                                    onChange={(event) => handleUpdate({ dueDate: event.format("YYYY-MM-DDTHH:mm:ss.SSS") })}
                                />
                            </LocalizationProvider>

                            <TextField
                                fullWidth
                                data-testid="task-description"
                                label={t('description')}
                                multiline
                                value={newTask.description}
                                onChange={(event) => handleUpdate({ description: event.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <VoiceInput
                                            value={newTask.description}
                                            onResult={(text) => handleUpdate({ description: text })}
                                        />
                                    )
                                }}
                            />
                        </Stack>
                    </Box>
                </Fade>

            </Stack>
        </Fade>
    );
}

export default Task;
